<script>
import agreementStats from '@/utils/agreementStats';
import modelCustomSettings from '@/model/userCustomSettings';

import FormBlock from '@/components/FormBlock.vue';
import BaseAccordion from '@/components/BaseAccordion.vue';
import BtnExpand from '@/components/ui/BtnExpand.vue';
import CommentTile from '@/components/CommentTile.vue';
import FileList from '@/components/FileList.vue';
import AgreementTile from '@/components/task/detail/AgreementTile.vue';
import BaseCheckbox from '@/components/BaseCheckbox.vue';
import SelectableList from '@/components/ui/SelectableList.vue';
import UserDetailStatisticTileItem from '@/components/user/tiles/UserDetailStatisticTileItem.vue';
import StatisticTile from '@/components/ui/StatisticTile.vue';
import BaseLink from '@/components/BaseLink.vue';
import EmptyList from '@/components/ui/EmptyList.vue';
import BaseSelect from '@/components/BaseSelect.vue';
import BaseSelectMulti from '@/components/BaseSelectMulti.vue';

export default {
  name: 'AgreementList',
  components: {
    BaseSelectMulti,
    BaseSelect,
    EmptyList,
    BaseLink,
    SelectableList,
    BaseCheckbox,
    FormBlock,
    BaseAccordion,
    BtnExpand,
    CommentTile,
    FileList,
    AgreementTile,
    UserDetailStatisticTileItem,
    StatisticTile,
  },
  props: {
    stages: Array,
    tasks: Object,
    agreements: Array,
    externalContacts: Array,
  },
  data() {
    return {
      selectedStageIds: new Set(),
      tasksOfSelectedStages: new Set(),
      selectedExternalContacts: [],
      filters: {
        created: true,
        confirmed: true,
        rejected: true,
      },
      printable: false,
    };
  },
  computed: {
    statuses() {
      return [
        { code: 'created', title: 'Ожидает согласования' },
        { code: 'confirmed', title: 'Согласовано' },
        { code: 'rejected', title: 'Отправлено на доработку' },
      ];
    },
    externalContactOptions() {
      return [
        //{ value: 'none', label: 'Без внешнего пользователя' },
        ... this.externalContacts.map(c => ({ value: c.id, label: c.name })),
      ];
    },
    stagesWithAgreements() {
      const taskIds = new Set(this.agreements.reduce((acc, val) => [...acc, val.comment.task], []));
      const stageIds = new Set(
        Object.entries(this.tasks)
          .map(([key, val]) => [key, val.filter(task => taskIds.has(task['@id']))])
          .filter(([, val]) => val.length)
          .map(([key]) => parseInt(key, 10))
      );

      return this.stages.filter(stage => stageIds.has(stage.id));
    },
    agreementsFiltered() {
      return this.agreements
        .filter(a =>
            (this.tasksOfSelectedStages.has(a.comment.task))
          && (!this.selectedExternalContacts.length || this.selectedExternalContacts.includes(a.externalContact?.id ?? 'none'))
          && (
              (this.filters.created && a.isAgreed === null)
            || (this.filters.confirmed && a.isAgreed === true)
            || (this.filters.rejected && a.isAgreed === false))
          )
    },
    commentsByTask() {
      return this.agreementsFiltered
        .reduce((acc, val) => {
          acc[val.comment.task] ??= [];
          acc[val.comment.task].push(val);
          return acc;
        }, {});
    },
    tasksFiltered() {
      const taskIds = Object.keys(this.commentsByTask);

      const entries = Object.entries(this.tasks)
        .map(([key, val]) => [key, val.filter(task => taskIds.includes(task['@id']))])
        .filter(([, val]) => val.length);

      return Object.fromEntries(entries);
    },
    stagesFiltered() {
      const stageIds = Object.keys(this.tasksFiltered).map(id => parseInt(id, 10));
      return this.stages.filter(stage => stageIds.includes(stage.id));
    },
    statsTotal() {
      return agreementStats.getStats(this.agreementsFiltered);
    },
    statsByStage() {
      const entries = this.stagesFiltered.map(stage => {
        const agreements = this.tasksFiltered[stage.id]
          .reduce((acc, val) => [
            ...acc,
            ...(this.commentsByTask[val['@id']] ?? [])
          ], []);

        return [stage.id, agreementStats.getStats(agreements)];
      });

      return Object.fromEntries(entries);
    },
    statTitles() { return agreementStats.getTitles(); },
    currUser() { return this.$store.state.auth.user; },
    foldedStages() { return this.currUser.customSettings.foldedStagesInAgreements ?? []; },
  },
  watch: {
    stagesWithAgreements() { this.selectAllStages(); },
    agreements() { this.selectAllStages(); },
  },
  mounted() {
    const script = document.createElement('script');
    script.id = 'html2pdf';
    script.type = 'application/javascript';
    script.src = 'https://cdnjs.cloudflare.com/ajax/libs/html2pdf.js/0.10.1/html2pdf.bundle.min.js';
    document.head.appendChild(script);
  },
  beforeDestroy() {
    document.getElementById('html2pdf').remove();
  },
  methods: {
    selectAllStages() {
      if (!this.stages.length) return;
      if (!this.agreements.length) return;
      this.$nextTick(() => {
        this.$refs.stageSelectableList?.selectAll(true);
      });
    },
    toggleStageFolding(stage) {
      const foldedStagesInAgreements = this.foldedStages.includes(stage.id)
        ? this.foldedStages.filter(id => id !== stage.id)
        : [...this.foldedStages, stage.id];

      modelCustomSettings.set({ foldedStagesInAgreements });
    },
    onStageSelect(indexes) {
      this.tasksOfSelectedStages = new Set(
        indexes.reduce(
          (acc, i) => [
            ...acc,
            ...(this.tasks[this.stagesWithAgreements[i]?.id] ?? []).map(t => t['@id']),
          ],
          [],
        ),
      );
    },
    exportPdf() {
      this.printable = true;
      window.scrollTo({top: 0});

      const interval = setInterval(() => {
        if (!window.html2pdf) return;
        clearInterval(interval);

        var opt = {
          margin: [0.7, 0, 0.7, 0],
          pagebreak: { mode: ['avoid-all'], },
          // image: { type: 'jpg', quality: 0.9 },
          // pagebreak: { avoid: ['.comment', '.stage-stats'], },
          html2canvas: { scale: 5, useCORS: true, dpi: 192, letterRendering: true, },
          jsPDF: { unit: "cm", format: "a4", orientation: "portrait" },
        };

          window.html2pdf()
            .set(opt)
            .from(this.$refs.main)
            .save()
            .then(() => { this.printable = false; });
      }, 100);
    },
  },
};
</script>

<template lang="pug">
  .b-agreement-list(:class="{ printable }")
    ._main(ref="main" :class="{ printable }")
      FormBlock._stats-wrap(mobile)
        ._title Статистика согласований
          Icon._logo(name="logo" colored)
        ._statistics
          StatisticTile(
            v-for="key in Object.keys(statTitles)"
            :key="statTitles[key].title"
            v-bind="statTitles[key]"
            :value="statsTotal[key]"
          )
          ._stat-desc В статистике учитываются только рабочие дни календаря по московскому часовому поясу
      FormBlock(mobile)._filters
        template(v-for="status in statuses")
          BaseCheckbox(v-model="filters[status.code]" :label="status.title" :class="[status.code]")
        BaseSelectMulti._filter-ext-contacts(
          :options="externalContactOptions"
          v-model="selectedExternalContacts"
          notSelectedLabel="Внешние пользователи"
          label="Внешние пользователи"
        )
      FormBlock._empty(v-if="!agreements.length")
        EmptyList(label="В проекте нет согласований")
      template(v-else)
        FormBlock(mobile).b-agreement-stage._stage(v-for="stage in stagesFiltered" :key="stage['@id']")
          ._header
            ._title {{ stage.name }}
            BtnExpand(:expanded="!foldedStages.includes(stage.id)" @toggle="toggleStageFolding(stage)")
          ._content(v-if="printable || !foldedStages.includes(stage.id)")
            BaseAccordion._task(
              v-for="task in tasksFiltered[stage.id]"
              :key="task.id"
              :title="task.name"
            )
              ._agreements
                CommentTile(
                  v-for="agreement in commentsByTask[task['@id']]"
                  :key="agreement.id"
                  :comment="agreement.comment"
                  :taskId="task.id"
                  external
                )
                  template(#linkToTask)
                    BaseLink._go-to-task(:to="{ name: 'task-detail', params: { projectId: $route.params.projectId, stageId: stage.id, taskId: task.id }, query: { comment: agreement.comment.id } }") Перейти к комментарию
                  template(#files v-if="agreement.comment.projectFiles.length")
                    FileList(:files="agreement.comment.projectFiles" downloadAll zoom)
                  template(#footer)
                    AgreementTile(:item="agreement" @deleted="$emit('agreementDeleted', agreement)")
                      template(#files v-if="agreement.answeredAt && agreement.projectFiles.length")
                        FileList._files(:files="agreement.projectFiles")
          ._footer
            .b-stage-stats
              ._item(v-for="key in Object.keys(statTitles)") {{ statTitles[key].title }}
                ._value {{ statsByStage[stage.id][key] }}
    FormBlock(mobile v-if="agreements.length")._aside
      ._aside-title Разделы
      SelectableList._aside-stages(
        :items="stagesWithAgreements"
        v-slot="{ items, checked, toggle, enter, leave, down }"
        ref="stageSelectableList"
        @change="onStageSelect"
      )
        ._aside-stage(v-for="(stage, i) in items" :key="stage.id" @mouseenter="enter(i)") {{ stage.name }}
          BaseCheckbox(
            :value="checked.has(i)"
            @toggle="toggle(i, $event)"
            @mouseleave.native="leave(i)"
            @mousedown.native="down(i)"
          )
      //Btn._export-pdf(@click="exportPdf")
      //  Icon(name="download" colored)
      //  | Экспортировать в PDF
</template>

<style lang="scss">
.printable {
  //background: green;

  .form-block {
    box-shadow: none !important;
    //display: contents !important;
    padding: 0 40px 0 30px !important;
    background: transparent;
  }

  .agreement-list {
    &__filters {
      display: none !important;
    }

    &__main {
      gap: 20px;
    }

    &__logo {
      display: block !important;
      color: #4BAAFD !important;
    }

    &__title {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 20px !important;
    }
  }

  .accordion__header {
    display: none !important;
  }

  .accordion__content {
    display: block !important;
  }

  .comment {
    margin: 0 !important;
  }

  .comment__author-img {
    display: none !important;
  }

  .agreement-stage {
    gap: 20px !important;
  }

  .agreement-stage__content {
    order: 10 !important;
  }

  .agreement-stage__agreements {
    gap: 20px !important;
  }

  .agreement-stage__title button {
    display: none !important;
  }

  .agreement-tile__buttons {
    display: none !important;
  }



  .file__menu,
  .file__size
  {
    display: none !important;
  }

  .file-list__dl-all {
    display: none !important;
  }

  .file__download {
    display: block !important;
    color: transparent;
    font-size: 0;
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;

    //&:before {
    //  content: 'https://teroom';
    //  color: #000;
    //  font-size: 11px;
    //}
    //
    //&:after {
    //  content: attr(href);
    //  color: #000;
    //  font-size: 11px;
    //}

    svg {
      display: none;
    }
  }

  .file__date,
  .file__name {
    display: block !important;
  }

  .comment__link-to-task {
    display: none !important;
  }



  .stage-stats {
    grid-template-columns: 1fr 1fr 1fr 1fr !important;
    row-gap: 30px;

    &__item {
      &:nth-child(4n + 1) {
        padding-left: 0 !important;
      }
      &:nth-child(4n) {
        border-right: none !important;
        padding-right: 0 !important;
      }
    }
  }


}
</style>

<style scoped lang="scss">
.agreement-list {
  display: grid;
  grid-template-columns: 1fr 330px;
  gap: 40px;

  &__logo {
    display: none;
  }

  &__empty {
    grid-column: 1 / -1;
  }

  &__stat-desc {
    font-size: 11px;
    padding: 20px 16px;
    border-radius: 8px;
    font-weight: 400;
    color: #1E3147;
    background: #E1F0FE;
  }

  &__title {
    font-size: 18px;
    margin-bottom: 12px;
  }

  &__filters {
    display: flex;
    gap: 20px;

    ::v-deep .checkbox-field.created .label {
      color: #FFB27B;
    }

    ::v-deep .checkbox-field.rejected .label {
      color: #F17275;
    }

    ::v-deep .checkbox-field.confirmed .label {
      color: #6CD796;
    }
  }

  &__filter-ext-contacts {
    margin-left: auto;
  }

  &__main {
    display: flex;
    flex-direction: column;
    gap: 38px;
  }

  &__stats-wrap {
    align-self: start;
  }

  &__statistics {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 23px;
  }

  &__aside {
    align-self: start;

    &-title {
      padding-bottom: 22px;
      margin-bottom: 13px;
      border-bottom: 1px solid #EBEFF2;
    }

    &-stages {
      display: flex;
      flex-direction: column;
      gap: 16px;
      //margin-bottom: 30px;
    }

    &-stage {
      display: flex;
      flex-direction: row-reverse;
      justify-content: flex-end;
      align-items: center;
      padding: 16px 20px;
      background: #F8F9FC;
      border-radius: 6px;
      font-size: 14px;
    }
  }
  &__export-pdf {
    margin-top: 30px;
    width: 100%;
    //display: block;
  }

  @media (max-width: 1350px) {
    grid-template-columns: 1fr 1fr;
    gap: 20px;

    &__export-pdf {
      display: none;
    }

    &__main {
      display: contents;
    }

    &__aside {
      grid-column: 2;
      grid-row: 1;
    }

    &__stage {
      grid-column: 1 / -1;
    }

    &__filters {
      grid-column: 1 / -1;
    }

    &__statistics {
      grid-template-columns: 1fr 1fr;
      gap: 10px;
    }
  }

  @media (max-width: 900px) {
    &__statistics {
      grid-template-columns: 1fr;
    }
    &__filters {
      flex-wrap: wrap;
    }
    &__filter-ext-contacts {
      width: 100%;
    }
  }

  @media (max-width: 576px) {
    grid-template-columns: 1fr;

    &__statistics {
      grid-template-columns: 1fr 1fr;
    }

    &__aside {
      grid-column: 1;
      grid-row: 2;
    }


  }

  @media (max-width: 400px) {
    grid-template-columns: 1fr;

    &__statistics {
      grid-template-columns: 1fr;
    }
  }
}

.agreement-stage {
  display: flex;
  flex-direction: column;
  gap: 45px;

  &__go-to-task {
    font-size: 12px;
    font-weight: 400;
    color: #33A1FB;
    text-align: right;
  }

  &__header {
    display: flex;
  }

  &__title {
    margin-right: auto;
  }

  &__agreements {
    display: flex;
    flex-direction: column;
    gap: 38px;
    margin-bottom: 40px;
  }
}

.stage-stats {
  background: #F8F9FC;
  padding: 14px 17px;
  border-radius: 8px;
  //display: flex;
  display: grid;
  grid-template-columns: repeat(7, 1fr);

  &__item {
    padding: 0 20px;
    border-right: 1px solid #000;
    font-size: 11px;
    font-weight: 400;
    color: #1E3147;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    //flex-shrink: 10;
    gap: 11px;


  }

  &__value {
    font-size: 16px;
    font-weight: 500;
  }

  @media (min-width: 900px) {
    &__item {
      &:first-child {
        padding-left: 0;
      }

      &:last-child {
        padding-right: 0;
        border: none;
      }
    }
  }

  @media (max-width: 899.98px) and (min-width: 576px) {
    grid-template-columns: 1fr 1fr 1fr 1fr;
    row-gap: 40px;

    &__item {
      &:nth-child(4n + 1) {
        padding-left: 0;
      }
      &:nth-child(4n) {
        border-right: none;
        padding-right: 0;
      }
    }
  }

  @media (max-width: 575.98px) and (min-width: 400px) {
    grid-template-columns: 1fr 1fr 1fr;
    row-gap: 40px;

    &__item {
      &:nth-child(3n + 1) {
        padding-left: 0;
      }
      &:nth-child(3n) {
        border-right: none;
        padding-right: 0;
      }
    }
  }

  @media (max-width: 399.98px) {
    grid-template-columns: 1fr 1fr;
    row-gap: 40px;

    &__item {
      &:nth-child(2n + 1) {
        padding-left: 0;
      }
      &:nth-child(2n) {
        border-right: none;
        padding-right: 0;
      }
    }
  }
}
</style>
